import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { getSubscriptionPrices } from "../../../api/services/subscription";
import { trackSubscriptionCheckoutEvent } from "../comps/SubscriptionCheckout/utils";
import { usePropertyFetcher } from "../hooks/usePropertyFetcher";
import { PropsData } from "../context";
import {
  mapPropertiesData,
  saveOrderToCookie,
  getOrderFromCookie,
  clearOrderFromCookie,
} from "../comps/SubscriptionCheckout/utils";
import { calculatePrices } from "../comps/SubscriptionCheckout/helpers";

const SubscriptionContext = createContext();

export const SubscriptionCheckoutContext = ({
  children,
  totalSteps = 3,
  initialStep,
  isOpen,
}) => {
  const propsContext = useContext(PropsData);
  const { fetchPropData } = usePropertyFetcher();

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(isOpen);

  const [userProperties, setUserProperties] = useState([]);
  const [subscriptionPrices, setSubscriptionPrices] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);

  const [selectedSubscription, setSelectedSubscription] = useState([]);

  const [couponDiscount, setCouponDiscount] = useState(undefined);
  const [extraData, setExtraData] = useState({});

  const [orderPrices, setOrderPrices] = useState({
    discountPrice: undefined,
    fullPrice: undefined,
    propertiesTotalPrice: undefined,
    propertiesHectarePrice: undefined,
    tax: undefined,
  });

  const [paymentMethod, setPaymentMethod] = useState(undefined);

  const [errorMessages, setErrorMessages] = useState({});

  useEffect(() => {
    setIsCheckoutOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    setCurrentStep(initialStep);

    if (initialStep === totalSteps) {
      const order = getOrderFromCookie();

      if (order) {
        setOrderPrices(order.orderPrices);
        setPaymentMethod(order.paymentMethod);
        setCouponDiscount(order.couponDiscount);
        setSelectedProperties(order.selectedProperties);
        setExtraData(order.extraData);

        clearOrderFromCookie();
      }
    }
  }, [initialStep]);

  useEffect(() => {
    if (currentStep === 2) {
      saveOrderToCookie({
        selectedProperties,
        extraData,
        orderPrices,
        paymentMethod,
        couponDiscount,
      });
    }
  }, [currentStep]);

  useEffect(() => {
    // Only run when checkout is open
    if (!isCheckoutOpen) return;
    trackSubscriptionCheckoutEvent("step_initiated", currentStep);

    if (currentStep === 3) {
      trackSubscriptionCheckoutEvent("completed", currentStep);
    }

    const fetchSubscriptionPrices = async () => {
      const { ok, status, data } = await getSubscriptionPrices();
      if (ok) {
        setSubscriptionPrices(data);
      } else {
        console.error("Price fetch error: ", status, data);
      }
    };

    const fetchProperties = async () => {
      const mappedProperties = mapPropertiesData(propsContext?.props);
      setUserProperties(mappedProperties);
    };

    fetchProperties();
    fetchSubscriptionPrices();
  }, [isCheckoutOpen]);

  useEffect(() => {
    if (!isCheckoutOpen) return;

    if (subscriptionPrices?.products && selectedProperties.length > 0) {
      const priceCalculation = calculatePrices({
        properties: selectedProperties,
        couponDiscount,
        subscriptionPrices,
      });

      setPaymentMethod(
        "klarna"
      );

      setOrderPrices((prev) => ({
        ...prev,
        discountPrice: priceCalculation.discountedPrice,
        fullPrice: priceCalculation.fullPrice,
        tax: 0.25 * priceCalculation.discountedPrice,
        propertiesHectarePrice: priceCalculation.propertiesHectarePrice,
        propertiesTotalPrice: priceCalculation.propertiesTotalPrice,
      }));
    } else {
      setOrderPrices((prev) => ({
        ...prev,
        discountPrice: undefined,
        fullPrice: undefined,
      }));
    }
  }, [isCheckoutOpen, subscriptionPrices, selectedProperties, couponDiscount]);

  // Memoize functions to prevent unnecessary re-renders
  const openCheckout = useCallback(() => setIsCheckoutOpen(true), []);
  const closeCheckout = useCallback(() => setIsCheckoutOpen(false), []);

  const addSelectedProperty = useCallback(
    (property) => {
      if (!selectedProperties.some((p) => p.ref === property.ref)) {
        setSelectedProperties([
          ...selectedProperties,
          {
            ...property,
            forestArea: Math.round(
              property?.forestArea ?? 0
            ),
          },
        ]);
      }
    },
    [selectedProperties]
  );

  const removeSelectedProperty = useCallback(
    (property) =>
      setSelectedProperties(
        selectedProperties.filter((p) => p.ref !== property.ref)
      ),
    [selectedProperties]
  );

  const setCurrentStepValue = useCallback(
    (step) =>
      step >= 1 && step <= totalSteps ? setCurrentStep(step) : currentStep,
    [currentStep, totalSteps]
  );

  const nextStep = useCallback(() => {
    if (currentStep === totalSteps) {
      closeCheckout();
      resetCheckout();
      return;
    }

    const nextStep = currentStep + 1;

    setCurrentStepValue(nextStep);
    trackSubscriptionCheckoutEvent("step_initiated", nextStep);

    if (nextStep === totalSteps) {
      trackSubscriptionCheckoutEvent("completed", nextStep, {
        payment_method: paymentMethod,
      });
    }
  }, [currentStep, setCurrentStepValue, totalSteps]);
  const previousStep = useCallback(
    () => setCurrentStepValue(currentStep - 1),
    [currentStep, setCurrentStepValue]
  );

  const resetCheckout = useCallback(() => {
    closeCheckout();
    setCurrentStep(1);
    setSelectedProperties([]);

    setErrorMessages({});
    setExtraData({});
    setCouponDiscount(undefined);
    setPaymentMethod(undefined);
    setOrderPrices({
      discountPrice: undefined,
      fullPrice: undefined,
      propertiesTotalPrice: undefined,
      propertiesHectarePrice: undefined,
      tax: undefined,
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      isCheckoutOpen,
      openCheckout,
      closeCheckout,

      // Property
      userProperties,
      addSelectedProperty,
      removeSelectedProperty,
      selectedProperties,

      // Subscription
      selectedSubscription,
      setSelectedSubscription,

      // Coupon discount
      couponDiscount,
      setCouponDiscount,

      // Extra data
      extraData,
      setExtraData,

      // Step
      currentStep,
      totalSteps,
      nextStep,
      previousStep,

      // Order prices
      orderPrices,
      setOrderPrices,

      // Payment method
      paymentMethod,
      setPaymentMethod,

      // Property prices
      subscriptionPrices,

      // Reset checkout
      resetCheckout,

      // Error messages
      errorMessages,
      setErrorMessages,
    }),
    [
      isCheckoutOpen,
      openCheckout,
      closeCheckout,
      userProperties,
      addSelectedProperty,
      removeSelectedProperty,
      selectedProperties,
      couponDiscount,
      setCouponDiscount,
      extraData,
      setExtraData,
      currentStep,
      totalSteps,
      nextStep,
      previousStep,
      orderPrices,
      setOrderPrices,
      resetCheckout,
      subscriptionPrices,
      paymentMethod,
      setPaymentMethod,
      errorMessages,
      setErrorMessages,
    ]
  );
  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};

export default SubscriptionContext;
